/**
 * PatientPortal — mobile-first magic-link portal for wellness patients.
 *
 * Sections (bottom-nav on mobile, sidebar on desktop):
 *  • Oggi      — recovery ring + today exercises checklist + streak
 *  • Recovery  — pain chart + posture timeline + sessions progress
 *  • Agenda    — upcoming appointments + confirm/cancel
 *  • Storico   — 12-week heatmap GitHub-style
 *  • Profilo   — claim account (email/password) + tenant info + documents
 *
 * Aesthetic: healthtech premium — calm gradient teal/emerald/cyan, soft shadows,
 * generous spacing, micro-animations. NO gamification badges. NO fitness vibe.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Activity, Home, Calendar, BarChart3, User, Check, Loader2,
  TrendingDown, TrendingUp, Heart, Sparkles, ChevronRight, X,
  Clock, Target, ArrowRight, FileText, Download, Camera, Phone,
  MapPin, ShieldCheck, LogIn, KeyRound, Mail, AlertCircle,
  CheckCircle2, Bell, BellOff, BellRing,
} from "lucide-react";
import { usePatientPush, PUSH_STATES } from "../hooks/usePatientPush";

const fmtDate = (iso, opts) => iso
  ? new Date(iso).toLocaleDateString("it-IT", opts || { day: "2-digit", month: "short" })
  : "—";

const fmtTime = (t) => t || "";

const PatientPortal = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const [tab, setTab]       = useState("today");
  const [err, setErr]       = useState(null);

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const r = await axios.get(`${API}/patient-portal/public/${token}`);
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e.response?.status === 404 ? "Link non valido o scaduto" : "Errore caricamento");
    } finally {
      setLoad(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-white to-emerald-50">
        <Loader2 className="w-10 h-10 text-teal-500 animate-spin" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-rose-50 to-amber-50">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto rounded-3xl bg-rose-100 flex items-center justify-center mb-4">
            <AlertCircle className="w-7 h-7 text-rose-500" />
          </div>
          <h1 className="text-2xl font-black text-slate-900">{err}</h1>
          <p className="text-sm text-slate-500 mt-2">Chiedi un nuovo link al tuo studio.</p>
          <Button onClick={() => navigate("/portal/login")} variant="outline" className="mt-6 rounded-full">
            <LogIn className="w-4 h-4 mr-1.5" /> Hai un account?
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 pb-24 lg:pb-8" data-testid="patient-portal">
      <Header data={data} />

      <main className="max-w-2xl mx-auto px-4 mt-4 space-y-4" data-testid="patient-portal-main">
        {tab === "today"    && <TodayTab data={data} token={token} onReload={load} />}
        {tab === "recovery" && <RecoveryTab data={data} />}
        {tab === "agenda"   && <AgendaTab data={data} token={token} onReload={load} />}
        {tab === "history"  && <HistoryTab data={data} />}
        {tab === "profile"  && <ProfileTab data={data} token={token} onReload={load} />}
      </main>

      <BottomNav tab={tab} setTab={setTab} />
    </div>
  );
};

/* ============================================================ */
/* HEADER                                                       */
/* ============================================================ */
const Header = ({ data }) => (
  <header className="max-w-2xl mx-auto px-4 pt-8 pb-2">
    <div className="flex items-center gap-3">
      {data.tenant.logo_url ? (
        <img
          src={data.tenant.logo_url}
          alt={data.tenant.name}
          className="w-11 h-11 rounded-2xl object-cover shadow"
        />
      ) : (
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow">
          <Heart className="w-5 h-5 text-white" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-teal-600">Recovery Journey</p>
        <h1 className="text-lg font-black text-slate-900 truncate" data-testid="portal-greeting">
          Ciao {data.client.first_name}!
        </h1>
        <p className="text-[11px] text-slate-500 truncate">{data.tenant.name}</p>
      </div>
    </div>
  </header>
);

/* ============================================================ */
/* BOTTOM NAV (mobile)                                          */
/* ============================================================ */
const NAV_ITEMS = [
  { key: "today",    label: "Oggi",     icon: Home },
  { key: "recovery", label: "Recovery", icon: Activity },
  { key: "agenda",   label: "Agenda",   icon: Calendar },
  { key: "history",  label: "Storico",  icon: BarChart3 },
  { key: "profile",  label: "Profilo",  icon: User },
];

const BottomNav = ({ tab, setTab }) => (
  <nav
    className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-xl border-t border-slate-200 z-50 lg:max-w-2xl lg:mx-auto lg:bottom-4 lg:rounded-3xl lg:border lg:shadow-2xl"
    data-testid="patient-portal-nav"
  >
    <div className="grid grid-cols-5 max-w-2xl mx-auto px-1 py-1.5">
      {NAV_ITEMS.map(it => {
        const Icon = it.icon;
        const active = tab === it.key;
        return (
          <button
            key={it.key}
            onClick={() => setTab(it.key)}
            className={`flex flex-col items-center gap-0.5 py-2 rounded-2xl transition-all ${
              active ? "bg-gradient-to-br from-teal-500/10 to-emerald-500/10" : "hover:bg-slate-50"
            }`}
            data-testid={`portal-nav-${it.key}`}
          >
            <Icon className={`w-5 h-5 transition-colors ${active ? "text-teal-600" : "text-slate-400"}`} />
            <span className={`text-[10px] font-bold transition-colors ${active ? "text-teal-700" : "text-slate-500"}`}>
              {it.label}
            </span>
          </button>
        );
      })}
    </div>
  </nav>
);

/* ============================================================ */
/* TODAY TAB                                                    */
/* ============================================================ */
const TodayTab = ({ data, token, onReload }) => {
  const totalExercises = data.routines.reduce((acc, r) => acc + r.exercises.length, 0);
  return (
    <div className="space-y-4" data-testid="portal-today">
      <RecoveryRing recovery={data.recovery} today={data.today} />

      {/* Greeting card */}
      <div className="rounded-3xl bg-white shadow-sm border border-slate-200/60 p-4">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-teal-600">{fmtDate(new Date().toISOString(), { weekday: "long", day: "2-digit", month: "long" })}</p>
        <h2 className="text-lg font-black text-slate-900 mt-1">
          {data.today.percent === 100 ? "Hai completato tutto. Ottimo lavoro 🌿" :
           totalExercises === 0      ? "Nessun esercizio assegnato per oggi" :
                                       `Ancora ${totalExercises - data.today.completed} esercizi da completare`}
        </h2>
        {data.today.current_streak > 0 && (
          <p className="text-xs text-slate-500 mt-1">
            <span className="font-bold text-emerald-600">{data.today.current_streak} giorni</span> consecutivi di costanza
          </p>
        )}
      </div>

      {data.routines.length === 0 ? (
        <EmptyCard
          icon={Sparkles}
          title="Nessuna routine attiva"
          msg="Il tuo professionista non ti ha ancora assegnato esercizi. Tornaci dopo la prossima visita."
        />
      ) : (
        data.routines.map(r => (
          <RoutineBlock key={r.routine_id} routine={r} token={token} onChanged={onReload} />
        ))
      )}
    </div>
  );
};

const RecoveryRing = ({ recovery, today }) => {
  const score = recovery.score;
  const R = 70;
  const C = 2 * Math.PI * R;
  const offset = C - (score / 100) * C;
  const tone = score >= 70 ? "emerald" : score >= 40 ? "teal" : "amber";
  const colors = {
    emerald: { from: "#10b981", to: "#34d399", text: "text-emerald-600" },
    teal:    { from: "#14b8a6", to: "#06b6d4", text: "text-teal-600" },
    amber:   { from: "#f59e0b", to: "#fbbf24", text: "text-amber-600" },
  }[tone];

  return (
    <div className="rounded-3xl bg-gradient-to-br from-teal-500 via-emerald-500 to-cyan-500 p-6 shadow-xl shadow-teal-500/20 text-white relative overflow-hidden" data-testid="recovery-ring">
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
      <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">Recovery Score</p>
      <div className="mt-2 flex items-center gap-5">
        <div className="relative">
          <svg viewBox="0 0 160 160" className="w-32 h-32 -rotate-90">
            <defs>
              <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%"  stopColor="white" stopOpacity="0.95" />
                <stop offset="100%" stopColor="white" stopOpacity="0.7" />
              </linearGradient>
            </defs>
            <circle cx="80" cy="80" r={R} stroke="rgba(255,255,255,0.18)" strokeWidth="12" fill="none" />
            <circle
              cx="80" cy="80" r={R}
              stroke="url(#ringGrad)" strokeWidth="12" fill="none"
              strokeLinecap="round"
              strokeDasharray={C}
              strokeDashoffset={offset}
              style={{ transition: "stroke-dashoffset 800ms cubic-bezier(0.4,0,0.2,1)" }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center flex-col">
            <p className="text-4xl font-black tabular-nums leading-none">{score}</p>
            <p className="text-[10px] uppercase tracking-wider font-bold opacity-70">su 100</p>
          </div>
        </div>
        <div className="flex-1 space-y-2 text-xs">
          <ComponentBar label="Riduzione dolore"  pct={recovery.pain_component}     />
          <ComponentBar label="Costanza esercizi" pct={recovery.adherence_component} />
          <ComponentBar label="Sedute completate" pct={recovery.sessions_component}  />
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
        <Pill label="Oggi" value={`${today.completed}/${today.total}`} />
        <Pill label="Costanza" value={`${today.current_streak} giorni`} />
      </div>
    </div>
  );
};

const ComponentBar = ({ label, pct }) => (
  <div>
    <div className="flex justify-between text-[10px] opacity-90 font-bold mb-0.5">
      <span>{label}</span><span>{pct}%</span>
    </div>
    <div className="h-1.5 rounded-full bg-white/20 overflow-hidden">
      <div className="h-full rounded-full bg-white/90 transition-all duration-700" style={{ width: `${pct}%` }} />
    </div>
  </div>
);

const Pill = ({ label, value }) => (
  <div className="rounded-2xl bg-white/15 backdrop-blur px-3 py-2 border border-white/20">
    <p className="text-[9px] uppercase tracking-wider font-bold opacity-80">{label}</p>
    <p className="font-black text-sm mt-0.5">{value}</p>
  </div>
);

const RoutineBlock = ({ routine, token, onChanged }) => {
  const [busy, setBusy] = useState({});

  const toggle = async (ex) => {
    setBusy(b => ({ ...b, [ex.code]: true }));
    try {
      const url = ex.done_today ? "exercise/undo" : "exercise/complete";
      await axios.post(`${API}/patient-portal/public/${token}/${url}`, {
        routine_id: routine.routine_id,
        exercise_code: ex.code,
      });
      if (!ex.done_today) toast.success("Ottimo lavoro 🌿");
      onChanged();
    } catch {
      toast.error("Riprova tra un momento");
    } finally {
      setBusy(b => ({ ...b, [ex.code]: false }));
    }
  };

  const done = routine.exercises.filter(e => e.done_today).length;
  const total = routine.exercises.length;

  return (
    <div className="rounded-3xl bg-white shadow-sm border border-slate-200/60 overflow-hidden" data-testid={`portal-routine-${routine.routine_id}`}>
      <div className="px-4 pt-4 pb-2 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-bold text-slate-900 truncate">{routine.name}</h3>
          <p className="text-[11px] text-slate-500">{total} esercizi</p>
        </div>
        <div className="text-right">
          <p className="font-black text-lg text-teal-600">{done}/{total}</p>
        </div>
      </div>
      <div className="px-4">
        <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-500"
            style={{ width: `${total ? (done / total) * 100 : 0}%` }}
          />
        </div>
      </div>
      <ul className="px-2 py-2 space-y-1">
        {routine.exercises.map((ex, idx) => (
          <li key={ex.code} className="flex items-start gap-3 p-2.5 rounded-2xl hover:bg-slate-50 transition-colors" data-testid={`portal-ex-${ex.code}`}>
            <button
              onClick={() => toggle(ex)}
              disabled={busy[ex.code]}
              className={`flex-shrink-0 w-9 h-9 rounded-2xl flex items-center justify-center transition-all shadow-sm ${
                ex.done_today
                  ? "bg-gradient-to-br from-emerald-500 to-teal-500 text-white"
                  : "bg-white border-2 border-slate-200 hover:border-teal-400"
              }`}
              data-testid={`portal-ex-toggle-${ex.code}`}
            >
              {busy[ex.code] ? <Loader2 className="w-4 h-4 animate-spin" /> : (ex.done_today ? <Check className="w-4 h-4" /> : <span className="text-slate-400 text-xs font-bold">{idx + 1}</span>)}
            </button>
            <div className="flex-1 min-w-0 pt-0.5">
              <p className={`text-sm font-bold ${ex.done_today ? "text-slate-400 line-through" : "text-slate-900"}`}>
                {ex.name}
              </p>
              <p className="text-[11px] text-slate-500">
                <Target className="inline w-3 h-3 mr-0.5" />
                {ex.sets}×{ex.reps}
                {ex.hold_sec > 0 && <> · <Clock className="inline w-3 h-3 mr-0.5" />{ex.hold_sec}s</>}
              </p>
              {ex.note && <p className="text-[10px] italic text-slate-400 mt-0.5">{ex.note}</p>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ============================================================ */
/* RECOVERY TAB                                                 */
/* ============================================================ */
const RecoveryTab = ({ data }) => {
  return (
    <div className="space-y-4" data-testid="portal-recovery">
      <PainChartCard pain={data.pain} />
      <SessionsCard sessions={data.sessions} />
      <PostureGrid posture={data.posture} />
    </div>
  );
};

const PainChartCard = ({ pain }) => {
  const history = pain.history || [];
  if (history.length === 0) {
    return (
      <div className="rounded-3xl bg-white shadow-sm border border-slate-200/60 p-5" data-testid="pain-chart-card">
        <SectionHeader icon={Heart} label="Dolore" />
        <p className="text-xs text-slate-400 text-center py-4">Ancora nessuna misurazione</p>
      </div>
    );
  }
  const W = 600, H = 100, P = 10;
  const xs = history.map((_, i) => P + (i * (W - 2 * P)) / Math.max(1, history.length - 1));
  const ys = history.map(s => H - (P + (s.score / 10) * (H - 2 * P)) + P);
  const points = xs.map((x, i) => `${x},${ys[i]}`).join(" L");
  const last = history[history.length - 1]?.score ?? 0;
  const trend = pain.initial != null && pain.latest != null ? pain.initial - pain.latest : 0;
  const stroke = last >= 6 ? "#ef4444" : last >= 3 ? "#f59e0b" : "#10b981";

  return (
    <div className="rounded-3xl bg-white shadow-sm border border-slate-200/60 p-5" data-testid="pain-chart-card">
      <SectionHeader icon={Heart} label="Evoluzione dolore" />
      <div className="flex items-center justify-between mt-2 mb-3">
        <div>
          <p className="text-xs text-slate-400">Iniziale</p>
          <p className="font-black text-2xl text-slate-300">{pain.initial ?? "—"}</p>
        </div>
        <div className="text-center">
          {trend > 0 ? (
            <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-bold">
              <TrendingDown className="w-3 h-3" /> Migliorato di {trend} punti
            </span>
          ) : trend < 0 ? (
            <span className="inline-flex items-center gap-1 text-rose-500 text-xs font-bold">
              <TrendingUp className="w-3 h-3" /> Aumentato di {Math.abs(trend)} punti
            </span>
          ) : (
            <span className="text-xs text-slate-400 font-bold">Stabile</span>
          )}
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-400">Attuale</p>
          <p className="font-black text-2xl" style={{ color: stroke }}>{pain.latest ?? "—"}</p>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H + 20}`} className="w-full h-auto">
        <defs>
          <linearGradient id="painPortalGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.3" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={`M${points} L${xs[xs.length-1]},${H + P} L${xs[0]},${H + P} Z`} fill="url(#painPortalGrad)" />
        <path d={`M${points}`} fill="none" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {xs.map((x, i) => (
          <circle key={i} cx={x} cy={ys[i]} r="3" fill="white" stroke={stroke} strokeWidth="2" />
        ))}
      </svg>
    </div>
  );
};

const SessionsCard = ({ sessions }) => (
  <div className="rounded-3xl bg-white shadow-sm border border-slate-200/60 p-5" data-testid="portal-sessions-card">
    <SectionHeader icon={Activity} label="Pacchetti sedute" />
    {sessions.active_packages === 0 ? (
      <p className="text-xs text-slate-400 text-center py-4">Nessun pacchetto attivo</p>
    ) : (
      <>
        <div className="flex items-center justify-between mt-3 mb-2">
          <div>
            <p className="text-xs text-slate-500">Sedute rimaste</p>
            <p className="font-black text-3xl text-slate-900">
              {sessions.remaining}<span className="text-base text-slate-400 font-normal">/{sessions.total_sessions}</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">Progresso</p>
            <p className="font-black text-3xl text-teal-600">{sessions.progress_pct}%</p>
          </div>
        </div>
        <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-700"
               style={{ width: `${sessions.progress_pct}%` }} />
        </div>
      </>
    )}
  </div>
);

const PostureGrid = ({ posture }) => (
  <div className="rounded-3xl bg-white shadow-sm border border-slate-200/60 p-5" data-testid="portal-posture-card">
    <SectionHeader icon={Camera} label="Foto posturali" />
    {posture.length === 0 ? (
      <p className="text-xs text-slate-400 text-center py-4">Nessuna foto condivisa</p>
    ) : (
      <div className="grid grid-cols-3 gap-2 mt-2">
        {posture.map(s => (
          <div key={s.session_id} className="aspect-square rounded-2xl overflow-hidden bg-slate-100 relative">
            {s.thumb_url ? (
              <img src={s.thumb_url} alt={s.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-300"><Camera className="w-6 h-6" /></div>
            )}
            <span className="absolute bottom-1 left-1 right-1 text-[9px] font-bold text-white px-1.5 py-0.5 rounded-full bg-black/50 backdrop-blur truncate">
              {fmtDate(s.captured_at)}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

/* ============================================================ */
/* AGENDA TAB                                                   */
/* ============================================================ */
const AgendaTab = ({ data, token, onReload }) => {
  if (data.appointments.length === 0) {
    return (
      <div data-testid="portal-agenda">
        <EmptyCard icon={Calendar} title="Nessun appuntamento" msg="Quando lo studio fisserà un appuntamento lo vedrai qui." />
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="portal-agenda">
      {data.appointments.map(a => (
        <AppointmentCard key={a.appointment_id} appt={a} token={token} onChanged={onReload} />
      ))}
    </div>
  );
};

const AppointmentCard = ({ appt, token, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const confirm = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/patient-portal/public/${token}/appointment/confirm`, {
        appointment_id: appt.appointment_id,
      });
      toast.success("Appuntamento confermato");
      onChanged();
    } catch {
      toast.error("Errore");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/patient-portal/public/${token}/appointment/cancel`, {
        appointment_id: appt.appointment_id,
        reason: "Annullato dal paziente",
      });
      toast.success("Appuntamento annullato");
      onChanged();
    } catch {
      toast.error("Errore");
    } finally {
      setBusy(false);
      setConfirmCancel(false);
    }
  };

  const isConfirmed = appt.status === "confirmed";

  return (
    <div className="rounded-3xl bg-white shadow-sm border border-slate-200/60 overflow-hidden" data-testid={`portal-appt-${appt.appointment_id}`}>
      <div className={`p-4 ${isConfirmed ? "bg-gradient-to-br from-emerald-50 to-teal-50" : ""}`}>
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-teal-600">{fmtDate(appt.date, { weekday: "long", day: "2-digit", month: "long" })}</p>
            <h3 className="text-2xl font-black text-slate-900 mt-1">{fmtTime(appt.time) || "—"}</h3>
            <p className="text-sm text-slate-600 mt-1">{appt.service_name || appt.service || "Appuntamento"}</p>
            {appt.staff_name && <p className="text-xs text-slate-500 mt-0.5">con {appt.staff_name}</p>}
          </div>
          {isConfirmed && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500 text-white">
              <CheckCircle2 className="w-2.5 h-2.5" /> Confermato
            </span>
          )}
        </div>
        {!isConfirmed && (
          <div className="grid grid-cols-2 gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmCancel(true)}
              disabled={busy}
              className="rounded-full"
              data-testid={`portal-appt-cancel-${appt.appointment_id}`}
            >
              <X className="w-4 h-4 mr-1" /> Annulla
            </Button>
            <Button
              size="sm"
              onClick={confirm}
              disabled={busy}
              className="rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white"
              data-testid={`portal-appt-confirm-${appt.appointment_id}`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Conferma</>}
            </Button>
          </div>
        )}
        {confirmCancel && (
          <div className="mt-3 rounded-2xl bg-rose-50 border border-rose-200 p-3" data-testid={`portal-appt-confirm-cancel-${appt.appointment_id}`}>
            <p className="text-xs text-rose-700 font-bold mb-2">Sicuro di voler annullare?</p>
            <div className="grid grid-cols-2 gap-2">
              <Button size="sm" variant="outline" onClick={() => setConfirmCancel(false)} className="rounded-full">Indietro</Button>
              <Button size="sm" onClick={cancel} disabled={busy} className="rounded-full bg-rose-500 text-white hover:bg-rose-600">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Annulla"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ============================================================ */
/* HISTORY TAB (heatmap)                                        */
/* ============================================================ */
const HistoryTab = ({ data }) => {
  const heatmap = data.heatmap || [];
  // group into weeks (Sun start, 12 weeks)
  const weeks = [];
  let week = [];
  // pad first column up to Sunday alignment of first date
  if (heatmap.length > 0) {
    const firstDow = new Date(heatmap[0].date + "T00:00:00").getDay(); // 0..6
    for (let i = 0; i < firstDow; i++) week.push(null);
  }
  heatmap.forEach((d) => {
    week.push(d);
    if (week.length === 7) { weeks.push(week); week = []; }
  });
  if (week.length) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }

  const totalDays = heatmap.filter(d => d.count > 0).length;
  const totalCompletions = heatmap.reduce((acc, d) => acc + d.count, 0);

  return (
    <div className="space-y-4" data-testid="portal-history">
      <div className="rounded-3xl bg-white shadow-sm border border-slate-200/60 p-5">
        <SectionHeader icon={BarChart3} label="Ultime 12 settimane" />
        <div className="flex items-center gap-4 mt-2 mb-3">
          <div>
            <p className="text-xs text-slate-400">Giorni attivi</p>
            <p className="font-black text-2xl text-emerald-600">{totalDays}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Completamenti totali</p>
            <p className="font-black text-2xl text-slate-900">{totalCompletions}</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <div className="inline-flex gap-1" data-testid="portal-heatmap">
            {weeks.map((w, wi) => (
              <div key={wi} className="flex flex-col gap-1">
                {w.map((d, di) => {
                  if (!d) return <div key={di} className="w-3.5 h-3.5" />;
                  const intensity = d.count === 0 ? 0 : Math.min(4, d.count);
                  const bg = [
                    "bg-slate-100",
                    "bg-teal-200",
                    "bg-teal-400",
                    "bg-emerald-500",
                    "bg-emerald-600",
                  ][intensity];
                  return (
                    <div
                      key={di}
                      title={`${d.date} · ${d.count} esercizi`}
                      className={`w-3.5 h-3.5 rounded ${bg} transition-all hover:scale-125`}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3 text-[10px] text-slate-400">
          <span>Meno</span>
          <div className="w-3 h-3 rounded bg-slate-100" />
          <div className="w-3 h-3 rounded bg-teal-200" />
          <div className="w-3 h-3 rounded bg-teal-400" />
          <div className="w-3 h-3 rounded bg-emerald-500" />
          <div className="w-3 h-3 rounded bg-emerald-600" />
          <span>Più</span>
        </div>
      </div>
    </div>
  );
};

/* ============================================================ */
/* PROFILE TAB                                                  */
/* ============================================================ */
const ProfileTab = ({ data, token, onReload }) => {
  return (
    <div className="space-y-4" data-testid="portal-profile">
      {/* Tenant card */}
      <div className="rounded-3xl bg-white shadow-sm border border-slate-200/60 p-5">
        <SectionHeader icon={Heart} label="Il tuo studio" />
        <p className="font-black text-lg text-slate-900 mt-2">{data.tenant.name}</p>
        {data.tenant.address && <p className="text-xs text-slate-500 inline-flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {data.tenant.address}</p>}
        {data.tenant.phone && (
          <a href={`tel:${data.tenant.phone}`} className="block mt-3">
            <Button variant="outline" className="w-full rounded-full" data-testid="portal-contact-call">
              <Phone className="w-4 h-4 mr-1.5" /> {data.tenant.phone}
            </Button>
          </a>
        )}
      </div>

      <DocumentsCard documents={data.documents} token={token} />

      <NotificationsCard token={token} />

      <AccountCard account={data.account} token={token} onClaimed={onReload} />
    </div>
  );
};

const DocumentsCard = ({ documents, token }) => (
  <div className="rounded-3xl bg-white shadow-sm border border-slate-200/60 p-5" data-testid="portal-docs-card">
    <SectionHeader icon={FileText} label="Documenti condivisi" />
    {documents.length === 0 ? (
      <p className="text-xs text-slate-400 text-center py-4">Nessun documento ancora</p>
    ) : (
      <ul className="space-y-1.5 mt-2">
        {documents.map(d => (
          <li key={d.document_id} className="flex items-center gap-3 p-2.5 rounded-2xl hover:bg-slate-50" data-testid={`portal-doc-${d.document_id}`}>
            <div className="w-9 h-9 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 text-teal-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-900 truncate">{d.filename || d.title || d.category}</p>
              <p className="text-[10px] text-slate-400">{fmtDate(d.created_at)}</p>
            </div>
            <a
              href={`${API}/patient-portal/public/${token}/document/${d.document_id}`}
              target="_blank" rel="noreferrer"
              className="text-teal-600 hover:bg-teal-50 rounded-full p-2"
              data-testid={`portal-doc-dl-${d.document_id}`}
            >
              <Download className="w-4 h-4" />
            </a>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const NotificationsCard = ({ token }) => {
  const { state, busy, subscribe, unsubscribe } = usePatientPush(token);

  if (state === PUSH_STATES.unsupported) {
    return (
      <div className="rounded-3xl bg-white shadow-sm border border-slate-200/60 p-5 opacity-70" data-testid="portal-push-card">
        <SectionHeader icon={BellOff} label="Notifiche" />
        <p className="text-xs text-slate-400 mt-2">Il tuo dispositivo non supporta le notifiche push.</p>
      </div>
    );
  }

  const isOn = state === PUSH_STATES.subscribed;
  const isDenied = state === PUSH_STATES.denied;

  return (
    <div
      className={`rounded-3xl shadow-sm border p-5 transition-all ${
        isOn
          ? "bg-gradient-to-br from-teal-500 to-emerald-500 text-white border-transparent shadow-lg shadow-teal-500/20"
          : "bg-white border-slate-200/60"
      }`}
      data-testid="portal-push-card"
    >
      <div className="flex items-center gap-2">
        <div className={`w-7 h-7 rounded-xl flex items-center justify-center ${
          isOn ? "bg-white/20" : "bg-gradient-to-br from-teal-500/15 to-emerald-500/15"
        }`}>
          {isOn ? <BellRing className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5 text-teal-600" />}
        </div>
        <p className={`text-[10px] uppercase tracking-[0.18em] font-bold ${isOn ? "opacity-90" : "text-teal-600"}`}>
          {isOn ? "Notifiche attive" : "Notifiche"}
        </p>
      </div>

      <p className={`text-xs mt-2 ${isOn ? "opacity-90" : "text-slate-500"}`}>
        {isOn
          ? "Riceverai un avviso quando il tuo studio assegna nuove routine o ti ricorda un appuntamento."
          : isDenied
            ? "Hai bloccato le notifiche per questo sito. Riattivale dalle impostazioni del browser."
            : "Attiva le notifiche per ricevere promemoria su nuovi esercizi e appuntamenti, anche con l'app chiusa."}
      </p>

      {!isDenied && (
        isOn ? (
          <Button
            onClick={unsubscribe}
            disabled={busy}
            variant="ghost"
            className="w-full mt-3 rounded-full bg-white/15 hover:bg-white/25 text-white border-0"
            data-testid="portal-push-disable"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><BellOff className="w-4 h-4 mr-1.5" /> Disattiva</>}
          </Button>
        ) : (
          <Button
            onClick={subscribe}
            disabled={busy}
            className="w-full mt-3 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white"
            data-testid="portal-push-enable"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Bell className="w-4 h-4 mr-1.5" /> Attiva notifiche</>}
          </Button>
        )
      )}
    </div>
  );
};


const AccountCard = ({ account, token, onClaimed }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [pwd, setPwd]   = useState("");
  const [saving, setSaving] = useState(false);

  if (account.claimed) {
    return (
      <div className="rounded-3xl bg-gradient-to-br from-teal-500 to-emerald-500 p-5 text-white shadow-lg" data-testid="portal-account-card">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5" />
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-90">Account attivo</p>
        </div>
        <p className="font-black text-lg mt-2 truncate">{account.claimed_email}</p>
        <p className="text-xs opacity-80 mt-1">Puoi entrare anche da un altro dispositivo con email e password.</p>
      </div>
    );
  }

  const submit = async () => {
    if (pwd.length < 8) { toast.error("Password troppo corta (min 8 caratteri)"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/patient-portal/public/${token}/claim`, { email, password: pwd });
      toast.success("Account creato 🌿");
      onClaimed();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-3xl bg-white shadow-sm border border-slate-200/60 p-5" data-testid="portal-account-card">
      <SectionHeader icon={KeyRound} label="Salva il tuo accesso" />
      <p className="text-xs text-slate-500 mt-2">
        Crea un account email/password per ritrovare facilmente il tuo recovery da qualsiasi dispositivo. Il link magico resta sempre valido.
      </p>
      {open ? (
        <div className="mt-3 space-y-2">
          <Input
            type="email"
            placeholder="La tua email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            data-testid="portal-claim-email"
            className="rounded-2xl"
          />
          <Input
            type="password"
            placeholder="Password (min 8 caratteri)"
            value={pwd}
            onChange={e => setPwd(e.target.value)}
            data-testid="portal-claim-password"
            className="rounded-2xl"
          />
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Annulla</Button>
            <Button
              onClick={submit}
              disabled={saving || !email || !pwd}
              className="rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white"
              data-testid="portal-claim-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Conferma"}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          onClick={() => setOpen(true)}
          variant="outline"
          className="w-full mt-3 rounded-full border-teal-200 text-teal-700"
          data-testid="portal-claim-open"
        >
          <Mail className="w-4 h-4 mr-1.5" /> Crea account
        </Button>
      )}
    </div>
  );
};

/* ============================================================ */
/* COMMON                                                       */
/* ============================================================ */
const SectionHeader = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2">
    <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-teal-500/15 to-emerald-500/15 flex items-center justify-center">
      <Icon className="w-3.5 h-3.5 text-teal-600" />
    </div>
    <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-teal-600">{label}</p>
  </div>
);

const EmptyCard = ({ icon: Icon, title, msg }) => (
  <div className="rounded-3xl bg-white shadow-sm border border-slate-200/60 p-8 text-center">
    <div className="w-12 h-12 mx-auto rounded-2xl bg-gradient-to-br from-teal-100 to-emerald-100 flex items-center justify-center mb-2">
      <Icon className="w-5 h-5 text-teal-500" />
    </div>
    <p className="font-bold text-slate-700">{title}</p>
    <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto">{msg}</p>
  </div>
);

export default PatientPortal;
